import { MultiValueProps, StylesConfig } from "react-select";
import AppSelect, { JevisSelectOption } from "../ui/AppSelect";
import { jevisSelectStyles } from "../ui/jevisSelectStyles";

/** Nasconde i chip interni al select: gli assegnatari si vedono solo sotto. */
function HiddenMultiValue(_props: MultiValueProps<JevisSelectOption, true>) {
  return null;
}

const BRAND = { primary: "#cd212a", secondary: "#2e436a" };

function displayName(label: string) {
  return label.split(" · ")[0].trim();
}

function initials(label: string) {
  const name = displayName(label);
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const assigneeMultiStyles: StylesConfig<JevisSelectOption, true> = {
  ...jevisSelectStyles("md"),
  control: (base, state) => ({
    ...jevisSelectStyles("md").control?.(base, state),
    minHeight: 44,
    padding: "4px 6px",
    borderColor: state.isFocused ? "rgba(205, 33, 42, 0.35)" : "rgba(46, 67, 106, 0.14)",
    backgroundColor: "#ffffff",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(205, 33, 42, 0.1), 0 4px 12px rgba(46, 67, 106, 0.06)"
      : "0 2px 8px rgba(46, 67, 106, 0.04)",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "4px 8px",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.875rem",
    borderRadius: "0.625rem",
    padding: "10px 12px",
    marginBottom: 2,
    backgroundColor: state.isSelected
      ? "rgba(46, 67, 106, 0.12)"
      : state.isFocused
        ? "rgba(205, 33, 42, 0.08)"
        : "transparent",
    color: BRAND.secondary,
    fontWeight: state.isSelected ? 700 : 500,
    "&:active": {
      backgroundColor: "rgba(205, 33, 42, 0.12)",
    },
  }),
};

function OptionWithAvatar({ label }: { label: string }) {
  const name = displayName(label);
  const isAdmin = label.includes("admin");
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2e436a] to-[#1e2d4a] text-[11px] font-bold text-white shadow-sm">
        {initials(label)}
      </span>
      <div className="min-w-0">
        <p className="truncate font-bold text-secondary">{name}</p>
        {isAdmin && <p className="text-[10px] font-semibold uppercase tracking-wide text-primary/80">Admin</p>}
      </div>
      <span className="ml-auto text-[10px] font-bold text-secondary/30">+</span>
    </div>
  );
}

interface AuditAssigneeMultiSelectProps {
  options: JevisSelectOption[];
  value: JevisSelectOption[];
  onChange: (value: JevisSelectOption[]) => void;
}

export default function AuditAssigneeMultiSelect({
  options,
  value,
  onChange,
}: AuditAssigneeMultiSelectProps) {
  const count = value.length;

  const removeOne = (userId: string) => {
    onChange(value.filter((o) => o.value !== userId));
  };

  return (
    <div className="overflow-hidden rounded-xl border border-secondary/12 bg-gradient-to-br from-white via-secondary/[0.02] to-primary/[0.03] shadow-sm ring-1 ring-secondary/5">
      <div className="flex items-center gap-2 border-b border-secondary/8 bg-secondary/[0.03] px-3 py-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary/10 text-sm" aria-hidden>
          👥
        </span>
        <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/55">
          Team · selezione multipla
        </p>
      </div>
      <div className="p-2.5">
        <AppSelect<true>
          isMulti
          options={options}
          value={value}
          onChange={(opts) => onChange([...(opts ?? [])])}
          placeholder={count > 0 ? "Aggiungi altri utenti..." : "Cerca e seleziona utenti..."}
          isClearable={count === 0}
          closeMenuOnSelect
          styles={assigneeMultiStyles}
          components={{ MultiValue: HiddenMultiValue }}
          formatOptionLabel={(opt) => <OptionWithAvatar label={opt.label} />}
          noOptionsMessage={() => "Nessun utente trovato"}
        />
        {count > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2 border-t border-secondary/8 pt-2.5">
            {value.map((opt) => (
              <span
                key={opt.value}
                className="inline-flex items-center gap-2 rounded-full bg-white py-1 pl-1 pr-2 shadow-sm ring-1 ring-secondary/12 transition hover:ring-primary/25"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#cd212a] to-[#a01820] text-[10px] font-bold text-white">
                  {initials(opt.label)}
                </span>
                <span className="max-w-[140px] truncate text-xs font-bold text-secondary">
                  {displayName(opt.label)}
                </span>
                <button
                  type="button"
                  onClick={() => removeOne(opt.value)}
                  className="ml-0.5 flex h-5 w-5 cursor-pointer items-center justify-center rounded-full text-secondary/35 transition hover:bg-red-50 hover:text-red-600"
                  aria-label={`Rimuovi ${displayName(opt.label)}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
